import gsap from 'gsap';
import '../../styles/index.scss';
import '../../styles/pages/index.scss';
import InfiniteGrid from '../components/infinite-grid';

export default class Index {
  constructor() {
    this.initLoadingCurtain();
    this.initCursor();
    this.initHeaderIntro();
    this.sources = [
      {
        src: 'alex-risio.webp',
        caption: 'Alexander Risio <br>Harvard AB/SM · Computer Science <br>Systems, intelligence, biology, ambitious software',
        back: {
          title: 'Alexander Risio',
          role: 'Harvard AB/SM · Computer Science',
          text: 'Coursework spanning systems programming, algorithms, machine learning, and computational biology. Interested in building ambitious software at the intersection of systems, intelligence, and biology — tools that are engineered to last and designed to matter.',
        },
      },
      {
        src: 'primeshio.webp',
        caption: 'Primeshio <br>AI-native 3D modeling <br>Generative geometry, graphics, high-performance pipelines',
        back: {
          title: 'Primeshio',
          role: 'Co-founder',
          text: 'Built from a passion for 3D modeling — an AI-powered modeling copilot for Blender. Received an in-person Y Combinator interview.',
          links: [{ label: 'primeshio.com', url: 'https://www.primeshio.com/' }],
        },
      },
      {
        src: 'arckis.webp',
        caption: 'Arckis <br>Smarter data ramp for government <br>Workflows, rules-aware tooling, public-sector systems',
        back: {
          title: 'Arckis',
          role: 'Co-founder',
          text: 'A better data rail for inter-agency communication in governments. Received a Y Combinator interview.',
          links: [{ label: 'GitHub', url: 'https://github.com/AlexRisio/Arckis' }],
        },
      },
      {
        src: 'hli-lab.webp',
        itemClass: 'item--inset',
        caption: 'HLI Lab <br>Harvard Medical School <br>Research for the HLI Lab — clinical ML, evaluation, deployment context',
        back: {
          title: 'HLI Lab',
          role: 'Research Assistant · Harvard Medical School',
          text: 'Implemented and benchmarked long-read alignment and variant-calling algorithms (e.g., minimap2, hifiasm) on large-scale human sequencing datasets. Optimized C/C++ and Python pipelines for genome assembly and pangenome analysis, improving runtime and memory efficiency on high-throughput sequencing data.',
          links: [
            { label: 'HLI Lab', url: 'https://hlilab.github.io/' },
            { label: 'Research', url: 'https://static-content.springer.com/esm/art%3A10.1038%2Fnature21429/MediaObjects/41586_2017_BFnature21429_MOESM26_ESM.pdf#page=12.07' },
          ],
        },
      },
      {
        src: 'hdag.webp',
        caption: 'Case Team Lead <br>HUDAG <br>Structured case work — research, analysis, delivery',
        back: {
          title: 'HDAG',
          role: 'Case Team Lead',
          text: 'Led teams of ~8 analysts building products and solutions still in use by client companies — including chatbots, market prediction models, and data pipelines.',
          links: [{ label: 'harvardanalytics.org', url: 'https://harvardanalytics.org/' }],
        },
      },
      {
        src: 'crimson.webp',
        itemClass: 'item--inset',
        caption: 'Technology Board <br>The Harvard Crimson <br>Editorial stack, reliability, daily publishing',
        back: {
          title: 'The Harvard Crimson',
          role: 'Technology Board',
          text: 'Work on updating the website for 600,000+ monthly visitors, providing both front-end and back-end solutions as well as data visualizations.',
          links: [{ label: 'thecrimson.com', url: 'https://www.thecrimson.com/' }],
        },
      },
      {
        src: 'cody-reading.webp',
        itemClass: 'item--inset',
        caption: 'Cody Reading <br>Lead data analyst <br>Analytics pipelines and stakeholder insight',
        back: {
          title: 'Cody Reading',
          role: 'Lead Data Analyst',
          text: 'Worked on data training a model for dyslexic readers.',
          links: [{ label: 'codyreading.com', url: 'http://codyreading.com/' }],
        },
      },
      {
        src: 'contact.webp',
        caption: 'Contact <br>ajrisio@college.harvard.edu <br>GitHub · LinkedIn',
        back: {
          title: 'Contact',
          role: "Here's how to reach me",
          links: [
            { label: 'ajrisio@college.harvard.edu', url: 'mailto:ajrisio@college.harvard.edu' },
            { label: 'LinkedIn', url: 'https://www.linkedin.com/in/alexanderrisio/' },
            { label: 'GitHub', url: 'https://github.com/AlexRisio' },
          ],
        },
      },
    ];
    this.desktopData = [
      { x: 110,  y: 100,  w: 320, h: 480 },  // alex-risio   (portrait 2:3)
      { x: 1060, y: 220,  w: 420, h: 224 },  // primeshio    (landscape)
      { x: 520,  y: 30,   w: 440, h: 222 },  // arckis       (landscape)
      { x: 880,  y: 860,  w: 280, h: 280 },  // hli-lab      (square)
      { x: 580,  y: 440,  w: 340, h: 191 },  // hdag         (16:9)
      { x: 30,   y: 800,  w: 240, h: 243 },  // crimson      (near-square)
      { x: 1240, y: 680,  w: 280, h: 280 },  // cody-reading (square)
      { x: 390,  y: 880,  w: 380, h: 254 },  // contact      (landscape)
    ];

    this.mobileData = [
      { x: 20,  y: 20,  w: 180, h: 270 },
      { x: 220, y: 50,  w: 200, h: 107 },
      { x: 20,  y: 350, w: 220, h: 111 },
      { x: 270, y: 310, w: 140, h: 140 },
      { x: 20,  y: 530, w: 220, h: 124 },
      { x: 270, y: 520, w: 120, h: 122 },
      { x: 20,  y: 720, w: 150, h: 150 },
      { x: 190, y: 740, w: 220, h: 147 },
    ];

    this.desktopSize = { w: 1600, h: 1320 };
    this.mobileSize = { w: 440, h: 1020 };

    this.grid = null;
    this.isMobile = null;

    window.addEventListener('resize', this.resize.bind(this));
    this.resize();
  }

  initGrid() {
    const nextIsMobile = window.matchMedia('(max-width: 900px)').matches;
    if (this.grid && this.isMobile === nextIsMobile) return;

    if (this.grid) this.grid.destroy();
    this.isMobile = nextIsMobile;

    this.grid = new InfiniteGrid({
      el: document.querySelector('#images'),
      sources: this.sources,
      data: this.isMobile ? this.mobileData : this.desktopData,
      originalSize: this.isMobile ? this.mobileSize : this.desktopSize,
    });
  }

  initLoadingCurtain() {
    const curtain = document.getElementById('loading-curtain');
    if (!curtain) return;
    gsap.to(curtain, {
      opacity: 0, duration: 0.8, ease: 'power2.inOut', delay: 0.15,
      onComplete: () => curtain.remove(),
    });
  }

  initCursor() {
    const cursor = document.querySelector('.cursor');
    if (!cursor || matchMedia('(pointer: coarse)').matches) return;

    let cx = window.innerWidth / 2;
    let cy = window.innerHeight / 2;
    let tx = cx;
    let ty = cy;

    window.addEventListener('mousemove', (e) => {
      tx = e.clientX;
      ty = e.clientY;
    });

    const interactives = 'a, button, .item-flip';
    document.addEventListener('mouseover', (e) => {
      if (e.target.closest(interactives)) cursor.classList.add('cursor--hover');
    });
    document.addEventListener('mouseout', (e) => {
      if (e.target.closest(interactives)) cursor.classList.remove('cursor--hover');
    });

    const tick = () => {
      cx += (tx - cx) * 0.15;
      cy += (ty - cy) * 0.15;
      cursor.style.transform = `translate(${cx}px, ${cy}px) translate(-50%, -50%)`;
      requestAnimationFrame(tick);
    };
    tick();
  }

  initHeaderIntro() {
    const h1 = document.querySelector('.frame h1');
    const links = document.querySelectorAll('.frame-left > a');
    if (!h1) return;

    gsap.set([h1, ...links], { opacity: 0, y: 12 });
    gsap.to(h1, {
      opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.3,
    });
    gsap.to([...links], {
      opacity: 1, y: 0, duration: 0.6, ease: 'power3.out',
      stagger: 0.07, delay: 0.55,
    });
  }

  resize() {
    document.documentElement.style.setProperty('--rvw', `${document.documentElement.clientWidth / 100}px`);
    this.initGrid();
  }
}
window.addEventListener('load', () => {
  new Index();
});
